import { api, csrfHeader } from '../api.js';
import { el, clear, openPanel, icon } from '../dom.js';
import { t, locale } from '../i18n.js';
import { LINK_MESSAGES } from './link-field.js';

// Two chevrons apart: open it all. Two together: fold it back into steps. The
// pair reads as one control in two states rather than as two commands.
const UNFOLD = 'M8 9.5 12 5.5l4 4M8 14.5l4 4 4-4';
const FOLD = 'M8 5.5l4 4 4-4M8 18.5l4-4 4 4';
const CLOSE = 'M6 6l12 12M18 6L6 18';
import { eventFields, describeMissing, MISSING_LABELS } from './event-fields.js';

// Three shells over one set of fields.
//
// **Wizard** for a new event: three slides, because the first one a venue
// enters is entered by somebody who has never seen this form. **Tabs** once
// the event exists, mirroring those slides, because what you learned creating
// it is where you look when you come back. **The full form** as an escape,
// remembered per account, because a wizard optimises the first event and taxes
// the twentieth — whoever is entering twelve dates does not want three slides
// twelve times.
//
// All three mount the same nodes from `event-fields.js` and go through the
// same save. A one-off is still a series, so every path lands on the same
// endpoint with the same permission check and the same export.
//
// The shell around them is a grid of three rows — head, body, foot — and the
// body is the only thing that scrolls. Head and foot are fixed by structure,
// not by `position: sticky`: there is no offset to get wrong, nothing can
// slide under the panel edge, and nothing inside the form can paint over them,
// because they are not siblings of the scrolling content.
export async function openSeriesForm({ venueId, series = null, date = null, accounts = null, onSaved }) {
  // Whose event this is, asked rather than inherited from wherever the form was
  // opened. A person maintains their own programme and some institutions, and
  // "the one I happened to be looking at" is the wrong default for a question
  // with a wrong answer — an event on the club's page instead of your own is
  // not a typo somebody notices.
  //
  // Only on a new one. Moving a published series between accounts changes whose
  // page it is on and whose bio link points at it, which is a different
  // operation from writing this form.
  const ownerId = venueId;
  const owners = series ? [] : (accounts ?? []).filter((row) => row.role === 'owner' || row.isSelf);

  // The account is what decides whether the place field is a toggle or a
  // required field, so the form waits for it rather than guessing.
  let account = await api.get(`/accounts/${ownerId}`).catch(() => null);

  let record = series;
  // What this dialog is doing, fixed when it opens. `record` is not that: the
  // wizard writes a draft on leaving the first slide — so abandoning it loses
  // nothing — and from that moment on the heading said "Termin bearbeiten"
  // over "Schritt 2 von 3". Somebody halfway through creating an event was
  // told they were editing one.
  const creating = !series;
  let mode = preference(venueId) === 'full' ? 'full' : (creating ? 'wizard' : 'tabs');
  let step = 0;

  const fields = eventFields({ account, series: record, date, csrf: csrfHeader() });

  // The state at the last successful save, so the way out can ask before it
  // throws input away.
  let savedState = JSON.stringify(fields.value());
  const dirty = () => JSON.stringify(fields.value()) !== savedState;

  // Reopened rather than rebuilt. Whose an event is decides whether the place
  // field is a toggle or a required question, so switching owner rebuilds every
  // field below it — and reopening is that rebuild, in three lines instead of a
  // second construction path that would drift from the first.
  //
  // It costs whatever was typed, which is why it is the first control on the
  // first slide: this is the question you answer before you start.
  function ownerPicker() {
    if (owners.length < 2) return null;
    const select = el('select', { id: 'ev-owner', 'aria-label': t.fieldOwner },
      owners.map((row) => el('option', { value: row.id }, row.isSelf ? t.ownProgramme : row.name)));
    select.value = String(ownerId);
    select.addEventListener('change', () => {
      dialog.close();
      openSeriesForm({ venueId: Number(select.value), date, accounts, onSaved });
    });
    return el('div', { class: 'field' }, [
      el('label', { for: 'ev-owner' }, t.fieldOwner), select,
      el('p', { class: 'card-meta' }, t.ownerHint),
    ]);
  }

  const heading = el('h2', { class: 'dialog-title' });

  // One exit, at the corner every panel puts it. It asks before it costs
  // anything: what was typed since the last save is gone when it closes. The
  // comparison is against the state at the last successful save, not against
  // "was anything ever typed".
  const leave = el('button', {
    class: 'icon-button ev-close', type: 'button',
    'aria-label': t.close, title: t.close,
  }, icon(CLOSE));
  leave.addEventListener('click', () => {
    if (dirty() && !window.confirm(t.discardChanges)) return;
    dialog.close();
  });

  const nav = el('div', { class: 'ev-nav' });

  // Errors and confirmations share one line, because the head is the only
  // region guaranteed to be on screen — one line for both means one place to
  // look. It clears on the next keystroke: it has been read once it is being
  // answered.
  const error = el('p', { class: 'pub-error', role: 'alert' });
  error.hidden = true;
  const notice = el('p', { class: 'card-meta', role: 'status' });
  notice.hidden = true;

  const head = el('div', { class: 'ev-head' }, [
    el('div', { class: 'ev-title-row' }, [heading, leave]),
    nav,
    error,
    notice,
  ]);

  const body = el('div', { class: 'ev-body' });

  // What is missing sits in the foot, above the button it blocks, each piece a
  // link to the station that asks for it. Where something is missing is
  // spatial and belongs on the station (the marker); what is missing is words
  // and belongs here.
  const gapLine = el('p', { class: 'ev-gaps' });
  gapLine.hidden = true;
  const footRow = el('div', { class: 'ev-foot-row' });
  const foot = el('div', { class: 'ev-foot' }, [gapLine, footRow]);

  // The foot is the last node: nothing renders below it, because nothing comes
  // after it.
  const form = el('form', { class: 'form form-wide event-form' }, [head, body, foot]);

  // The markers and the missing line are live: they follow the publisher's
  // typing rather than naming the gaps only after a button is pressed.
  // Listening on the form catches every control at once — chips are buttons,
  // tags are clicks, and a per-control subscription is what goes stale when a
  // field is added. Clicks repaint but never clear the message line: the
  // button that sets a message is itself a click.
  for (const event of ['input', 'change']) {
    form.addEventListener(event, () => queueMicrotask(() => { clearMessages(); paintLive(); }));
  }
  form.addEventListener('click', () => queueMicrotask(paintLive));

  // Two arrows apart to open the whole form, two together to fold it back into
  // steps. It sits beside the stations rather than under the buttons: it
  // changes what the *bar* is showing, and a full-width control at the bottom
  // of a scrolling dialog read as a third action beside Save and Cancel.
  const escape = el('button', { class: 'icon-button ev-escape', type: 'button' });
  escape.addEventListener('click', () => {
    mode = mode === 'full' ? (creating ? 'wizard' : 'tabs') : 'full';
    remember(venueId, mode === 'full' ? 'full' : 'guided');
    step = 0;
    paint();
  });

  paint();

  // A panel, not a dialog: see openPanel. Everything below still calls
  // `dialog.close()`, because the two containers answer the same surface.
  const dialog = openPanel(form);
  focusFirst();

  form.addEventListener('submit', (event) => {
    // Enter never means "publish". The primary action differs per shell, and a
    // form that publishes because somebody pressed Enter in the title field is
    // a form that publishes half-filled.
    event.preventDefault();
    if (mode === 'wizard' && step < fields.slides.length - 1) advance();
    else save({ message: record ? t.saved : t.createdDraft });
  });

  // ------------------------------------------------------------- painting

  // The stations are the three slides; the editor adds a fourth for the
  // actions that act on the whole series. The wizard never has it — a series
  // that is still being entered has nothing to pause, shift or delete — and
  // the full form has no stations at all.
  const stationCount = () => fields.slides.length + (mode === 'tabs' ? 1 : 0);

  function paint() {
    heading.textContent = creating ? t.newEvent : t.editEvent;

    const label = mode === 'full' ? t.useWizard : t.useFullForm;
    clear(escape);
    escape.append(icon(mode === 'full' ? FOLD : UNFOLD));
    // The word is gone from the surface, so it has to be on the control: an
    // icon-only button with no accessible name announces as "button".
    escape.setAttribute('aria-label', label);
    escape.setAttribute('title', label);

    clear(nav);
    clear(body);
    clear(footRow);

    if (mode === 'full') paintFull();
    else if (mode === 'tabs') paintTabs();
    else paintWizard();

    paintLive();
  }

  // The shape beside a station that is still missing something. Never colour
  // alone: it is a square, the station's own weight already carries
  // current-versus-not, and the words are in the line above the buttons.
  function marker(station) {
    return el('span', { class: 'ev-mark', 'aria-hidden': 'true', hidden: true, dataset: { station } });
  }

  function paintFull() {
    nav.append(escape);
    body.append(...[ownerPicker()].filter(Boolean));
    for (const slide of fields.slides) {
      body.append(el('h3', { class: 'ev-section' }, slide.label), ...slide.nodes);
    }
    // Folded away below everything else: these act on the whole series at
    // once and are not the equals of the fields above them.
    if (record) {
      body.append(el('details', { class: 'bulk' }, [
        el('summary', {}, t.bulkActions),
        bulkActions(record, onSaved, fail, () => dialog.close()),
      ]));
    }
    footRow.append(
      el('div', { class: 'row ev-foot-end' }, [saveButton(), publishButton()]),
    );
  }

  function paintTabs() {
    const tabs = el('div', {
      class: 'ev-tabs', role: 'tablist',
      'aria-label': t.stepOf(step + 1, stationCount()),
    });
    fields.slides.forEach((slide, index) => {
      tabs.append(tab(slide.key, slide.label, index));
    });
    // The series actions are a station, not a block that reappears under
    // every tab — and a station is not the footer, which was the constraint
    // on where they may sit.
    tabs.append(tab('series', t.stepSeries, fields.slides.length));

    tabs.addEventListener('keydown', (event) => {
      const delta = { ArrowLeft: -1, ArrowRight: 1 }[event.key];
      if (!delta) return;
      event.preventDefault();
      goTo((step + delta + stationCount()) % stationCount());
      nav.querySelector('[aria-selected="true"]')?.focus();
    });

    nav.append(tabs, escape);

    const onSeries = step === fields.slides.length;
    body.setAttribute('role', 'tabpanel');
    body.setAttribute('aria-labelledby', `ev-tab-${onSeries ? 'series' : fields.slides[step].key}`);
    if (onSeries) {
      body.append(bulkActions(record, onSaved, fail, () => dialog.close()));
    } else {
      if (step === 0) body.append(...[ownerPicker()].filter(Boolean));
      body.append(...fields.slides[step].nodes);
    }
    footRow.append(
      el('div', { class: 'row ev-foot-end' }, [saveButton(), publishButton()]),
    );
  }

  function tab(key, label, index) {
    const node = el('button', {
      class: 'ev-tab', type: 'button', role: 'tab',
      id: `ev-tab-${key}`,
      'aria-selected': index === step ? 'true' : 'false',
      tabindex: index === step ? '0' : '-1',
    }, [marker(key), label]);
    node.addEventListener('click', () => goTo(index));
    return node;
  }

  function paintWizard() {
    // "Schritt 1 von 3" is no longer a line of its own — the stations say
    // which one is current — so the count moves onto the list itself, where a
    // screen reader still gets it.
    const steps = el('ol', {
      class: 'wiz-steps',
      'aria-label': t.stepOf(step + 1, fields.slides.length),
    });
    fields.slides.forEach((slide, index) => {
      const entry = el('li', {
        class: `wiz-step${index === step ? ' wiz-step-current' : ''}`,
        'aria-current': index === step ? 'step' : null,
        tabindex: '0',
        role: 'button',
      }, [
        el('span', { class: 'wiz-step-n' }, String(index + 1)),
        marker(slide.key),
        el('span', {}, slide.label),
      ]);
      entry.addEventListener('click', () => goTo(index));
      steps.append(entry);
    });

    nav.append(steps, escape);
    if (step === 0) body.append(...[ownerPicker()].filter(Boolean));
    body.append(...fields.slides[step].nodes);

    const back = el('button', { class: 'button button-plain', type: 'button' }, t.back);
    back.addEventListener('click', () => goTo(step - 1));

    const forward = el('button', { class: 'button', type: 'button' }, t.next);
    forward.addEventListener('click', advance);

    // A draft is saveable from slide 1 onward — the single rule that makes a
    // three-step form safe, because abandonment stops being loss. It saves
    // and stays: closing is the ✕ and nothing else, because one button doing
    // both is why saving used to cost you your place in the form.
    const draft = el('button', { class: 'button button-quiet', type: 'button' }, t.saveDraft);
    draft.addEventListener('click', () => save({ message: record ? t.saved : t.createdDraft }));

    const last = step === fields.slides.length - 1;

    // The way back on the left, quiet and flush; the way forward on the
    // right, primary last. The row may wrap: in a grid row the foot grows
    // upward into its own space, never over the content.
    footRow.append(
      el('div', { class: 'row ev-foot-end' }, [
        draft,
        last ? publishButton() : forward,
      ]),
    );
    if (step > 0) footRow.prepend(el('div', { class: 'row ev-foot-start' }, [back]));
  }

  function paintLive() {
    const missing = fields.missing();

    // The marker has no second source of truth: a station is marked when a
    // missing field lives on it, and both lists come from the fields.
    const incomplete = new Set(missing.map((code) => fields.slideOf(code)));
    for (const mark of nav.querySelectorAll('.ev-mark')) {
      mark.hidden = !incomplete.has(mark.dataset.station);
    }

    clear(gapLine);
    if (missing.length > 0) {
      const labels = missing.map((code) => (MISSING_LABELS[code] ?? (() => code))());
      let index = 0;
      // formatToParts keeps the conjunction the locale asks for — "ein Titel
      // und ein Tag" — while each piece stays its own link.
      const parts = new Intl.ListFormat(locale, { type: 'conjunction' })
        .formatToParts(labels)
        .map((part) => {
          if (part.type !== 'element') return part.value;
          const code = missing[index++];
          const link = el('button', { class: 'ev-gap', type: 'button' }, part.value);
          link.addEventListener('click', () => {
            if (mode !== 'full') {
              goTo(fields.slides.findIndex((slide) => slide.key === fields.slideOf(code)));
            }
            fields.focus(code);
          });
          return link;
        });
      gapLine.append(...t.stillMissing(parts));
    }
    gapLine.hidden = missing.length === 0;
  }

  function focusFirst() {
    const first = body.querySelector('input:not([type=hidden]), textarea, select, button');
    first?.focus();
  }

  // --------------------------------------------------------------- actions

  // Repaints even when the station does not change. Re-entering the current
  // one is a real operation — it is what every "go to the thing that is
  // missing" path asks for, and the early return that used to sit here is how
  // the error once rendered above the visible edge with nothing moving to it.
  function goTo(index) {
    if (index < 0 || index >= stationCount()) return;
    step = index;
    paint();
    focusFirst();
  }

  // Leaving the first slide writes the draft. Two things follow from it that
  // are worth the round trip: the flyer starts uploading while the publisher
  // fills in the date — sharp re-encoding a phone photo is the slowest step in
  // the flow — and from here on closing the tab loses nothing.
  async function advance() {
    if (step === 0 && !(await save({ silent: true }))) return;
    goTo(step + 1);
  }

  function saveButton() {
    const node = el('button', { class: 'button', type: 'button' }, t.save);
    node.addEventListener('click', () => save({ message: record ? t.saved : t.createdDraft }));
    return node;
  }

  // Never disabled. A greyed-out button with no explanation is the thing the
  // gap list exists to replace: pressing it names what is missing and takes
  // the publisher to the slide that asks for it.
  function publishButton() {
    const live = record?.status === 'published';
    const node = el('button', { class: 'button', type: 'button' }, live ? t.unpublish : t.publish);
    node.addEventListener('click', () => (live ? unpublish() : publish()));
    return node;
  }

  async function save({ silent = false, message } = {}) {
    const payload = fields.value();
    // The requirement is real rather than defensive: without a title the
    // server has no row to create, so advancing would promise a draft that
    // does not exist. The date is pre-filled, so the title is the only thing
    // that blocks — and it is a first step, not a failure.
    if (!payload.title) {
      fail(t.titlePrompt);
      goTo(0);
      fields.invalidate('title');
      return false;
    }

    try {
      record = record
        ? await api.patch(`/series/${record.id}`, payload)
        : await api.post(`/accounts/${ownerId}/series`, payload);
      // Deliberately not awaited: the queued flyer uploads while the publisher
      // reads the next slide.
      fields.setSeriesId(record.id);
      savedState = JSON.stringify(fields.value());

      say(silent ? t.draftSaved : (message ?? t.saved));
      return true;
    } catch (problem) {
      // A rejected link names the field it came from; anything else is the
      // generic failure, because guessing at a cause is worse than admitting
      // there was one.
      fail(LINK_MESSAGES[problem?.code] ?? t.saveFailed);
      return false;
    }
  }

  async function publish() {
    const gaps = fields.missing();
    if (gaps.length > 0) {
      paintLive();
      goTo(fields.slides.findIndex((slide) => slide.key === fields.slideOf(gaps[0])));
      fields.focus(gaps[0]);
      return;
    }

    if (!(await save({ silent: true }))) return;

    try {
      await api.post(`/series/${record.id}/publish`);
      dialog.close();
      await onSaved(t.publishedNow);
    } catch (problem) {
      // The server computes the same list from the stored row, and both sides
      // word it from the same table — two different sentences for one rule is
      // what makes a publisher distrust the form.
      if (problem?.code === 'incomplete' && Array.isArray(problem.payload?.missing)) {
        fail(describeMissing(problem.payload.missing));
      } else {
        fail(t.saveFailed);
      }
    }
  }

  async function unpublish() {
    try {
      await api.post(`/series/${record.id}/unpublish`);
      dialog.close();
      await onSaved(t.unpublishedNow);
    } catch {
      fail(t.saveFailed);
    }
  }

  function fail(message) {
    notice.hidden = true;
    error.textContent = message;
    error.hidden = false;
  }

  function say(message) {
    error.hidden = true;
    notice.textContent = message;
    notice.hidden = false;
  }

  function clearMessages() {
    error.hidden = true;
    notice.hidden = true;
  }
}

// Which shell this person wants for this account, kept in the browser rather
// than on the row. "Remembered per account" needs no migration to be true:
// somebody maintaining a busy venue's twelve dates a month wants the full
// form there and may still want the wizard for the collective they run on the
// side. A preference that is wrong on a new device costs one click.
const PREF_KEY = 'tba.eventForm';

function preference(accountId) {
  try {
    return JSON.parse(window.localStorage.getItem(PREF_KEY) ?? '{}')[accountId] ?? null;
  } catch {
    return null;
  }
}

function remember(accountId, value) {
  try {
    const all = JSON.parse(window.localStorage.getItem(PREF_KEY) ?? '{}');
    all[accountId] = value;
    window.localStorage.setItem(PREF_KEY, JSON.stringify(all));
  } catch {
    // A browser refusing storage is not a reason to refuse the form.
  }
}

// The three things venues actually ask for. They are a station of their own
// in the editor — or the folded-away block at the end of the full form —
// because they act on the whole series at once and should not sit next to
// Save as if they were equals.
function bulkActions(series, onSaved, fail, close) {
  const from = el('input', { id: 'sf-pause-from', type: 'date' });
  const to = el('input', { id: 'sf-pause-to', type: 'date' });
  const pause = el('button', { class: 'button button-quiet', type: 'button' }, t.pauseSeries);

  const days = el('input', { id: 'sf-shift', type: 'number', value: '1', step: '1' });
  const shift = el('button', { class: 'button button-quiet', type: 'button' }, t.shiftSeries);

  const duplicate = el('button', { class: 'button button-quiet', type: 'button' }, t.duplicateSeries);

  // The one destructive control in the dialog, so it sits alone at the bottom
  // of the section, and it says what it will take with it before it does. An
  // archive rather than a deletion underneath — which is why one confirmation
  // is proportionate and a typed word is not.
  const remove = el('button', { class: 'button button-quiet is-danger', type: 'button' }, t.deleteSeries);
  remove.addEventListener('click', () => {
    if (!window.confirm(t.deleteSeriesConfirm(series.title, series.upcomingCount ?? 0))) return;
    run(() => api.del(`/series/${series.id}`), t.deletedSeries);
  });

  pause.addEventListener('click', () => run(
    () => api.post(`/series/${series.id}/pause`, { from: from.value, to: to.value }),
    t.paused,
  ));

  duplicate.addEventListener('click', () => run(
    () => api.post(`/series/${series.id}/duplicate`, { titleSuffix: t.copySuffix }),
    t.duplicated,
  ));

  shift.addEventListener('click', () => run(
    () => api.post(`/series/${series.id}/shift`, { days: Number(days.value) }),
    t.shifted,
  ));

  async function run(request, message) {
    try {
      await request();
      close();
      await onSaved(message);
    } catch (error) {
      fail(SHIFT_MESSAGES[error?.code] ?? t.actionFailed);
    }
  }

  // Pausing and shifting act on a *rule*: "the series carries on afterwards",
  // "every Friday plus one day becomes every Saturday". A one-off has no rule,
  // so those two are hidden rather than shown saying "the whole series" about
  // something that is one evening. Its date moves by dragging the chip in the
  // calendar, which is the same gesture and needs no sentence.
  const repeats = (series.recurrence?.freq ?? 'once') !== 'once';

  return el('div', { class: 'bulk-body' }, [
    ...(repeats ? [
      el('div', { class: 'row' }, [
        el('label', { for: 'sf-pause-from' }, t.pauseFrom), from,
        el('label', { for: 'sf-pause-to' }, t.pauseTo), to,
        pause,
      ]),
      el('p', { class: 'card-meta' }, t.pauseHint),
      el('div', { class: 'row' }, [
        el('label', { for: 'sf-shift' }, t.shiftSeries), days,
        el('span', {}, t.shiftDays), shift,
      ]),
      el('p', { class: 'card-meta' }, t.shiftHint),
    ] : []),
    el('div', { class: 'row' }, [duplicate]),
    el('div', { class: 'row bulk-danger' }, [remove]),
    el('p', { class: 'card-meta' }, t.deleteSeriesHint),
  ]);
}

const SHIFT_MESSAGES = {
  month_overflow: t.shiftMonthOverflow,
  setpos_not_shiftable: t.shiftNotShiftable,
};
